import { ListChecks } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { JournalTrade } from '../types'

/**
 * The pre-trade checklist, as it stood when the entry was taken — 9.8.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SKIPPED RULES ARE SHOWN, NOT FILTERED OUT
 *
 * The list of rules a trader CONFIRMED reads as a clean entry every time,
 * because the ones they waved through are exactly the ones missing from it.
 * Which rules were skipped is the finding; the ones that were followed are
 * the context it is read against.
 *
 * A SNAPSHOT, NOT A LOOKUP. These are the rules copied onto the deployment
 * at entry, not today's version of the strategy's checklist. A rule renamed,
 * reordered or deleted next week would otherwise restate what was confirmed
 * on a trade taken today — and the denominator would move with it, so "3 of
 * 7" would quietly become "3 of 5" on a review nobody reopened.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function JournalRules({ trade }: { trade: JournalTrade }) {
  const checklist = trade.checklist
  /*
   * Null is "not recorded" and empty is "this strategy asks nothing".
   * Neither is worth a panel, but they are not the same fact — the row's
   * RULES column shows an em dash for both and says which on its title.
   */
  if (!checklist || checklist.length === 0) return null

  const followed = checklist.filter((c) => c.ticked).length

  return (
    <section className="jn-panel" aria-label="Pre-trade checklist">
      <header className="jn-panel-heading">
        <div className="flex items-center gap-2">
          <ListChecks size={14} className="text-primary" />
          <h3>Rules</h3>
        </div>
        <span className="jn-badge num" data-testid="journal-rules-count">
          {followed} / {checklist.length} followed
        </span>
      </header>
      <ul className="jn-rules" data-testid="journal-checklist">
        {checklist.map((c, i) => (
          <li
            key={`${i}-${c.rule}`}
            className={cn('jn-rule', c.ticked ? 'on' : 'off')}
          >
            <span aria-hidden className="jn-rule-mark">
              {c.ticked ? '✓' : '×'}
            </span>
            <span className="jn-rule-text">{c.rule}</span>
            <span className="jn-rule-tag">{c.ticked ? 'Followed' : 'Skipped'}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}
